import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

// antd
import {
    Card,
    Col,
    Row,
    Segmented,
    Typography,
    ConfigProvider,
    Skeleton,
    theme,
} from 'antd'

// slices
import { talentState, updateTalentPermission } from '../talent.slice'

// css
import './index.css'
import { NoPermissionFoundIcon } from '../../../assets/icons/talent'

const { Title } = Typography

const primaryColor = '#4F46E5'

const EditPermission = () => {
    const { useToken } = theme

    const { token } = useToken()
    const { talentDetailData, permissionStatus, selectedData } =
        useSelector(talentState)

    const dispatch = useDispatch()

    const [permissions, setPermissions] = useState([])

    useEffect(() => {
        if (talentDetailData) {
            setPermissions(talentDetailData?.appPermission)
        }
    }, [talentDetailData])

    const handlePermissionChange = async (index, value) => {
        const updated = permissions.map((perm, i) =>
            i === index ? { ...perm, isAllowed: value === 'On' } : { ...perm }
        )

        setPermissions(updated)

        dispatch(
            updateTalentPermission({
                userId: selectedData?._id,
                appPermission: updated,
            })
        )
    }

    const loadingAppSkeleton = Array(5).fill(null) // or length of your expected data

    return (
        <ConfigProvider
            theme={{
                token: {
                    colorPrimary: primaryColor,
                },
            }}
        >
            <div
                style={{
                    margin: '20px 0px',
                    padding: '0 20px',
                }}
            >
                <Row
                    justify="space-between"
                    align="middle"
                    style={{ marginBottom: 16 }}
                >
                    <Col>
                        <Title
                            level={5}
                            style={{
                                margin: 0,
                                marginLeft: 20,
                                fontWeight: 600,
                            }}
                        >
                            Apps
                        </Title>
                    </Col>
                    <Col>
                        <Title
                            level={5}
                            style={{
                                margin: 0,
                                marginRight: 30,
                                fontWeight: 600,
                            }}
                        >
                            Permissions
                        </Title>
                    </Col>
                </Row>
                {permissionStatus === 'loading' ? (
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}
                    >
                        <Row gutter={[16, 16]}>
                            {loadingAppSkeleton.map((_, index) => (
                                <Col span={24} key={index}>
                                    <Card
                                        style={{
                                            borderRadius: 10,
                                            padding: '10px 16px',
                                            boxShadow:
                                                '0 8px 24px rgba(0,0,0,0.03)',
                                            border: '1px solid #f0f0f0',
                                        }}
                                        styles={{
                                            body: {
                                                padding: 0,
                                            },
                                        }}
                                    >
                                        <Row
                                            justify="space-between"
                                            align="middle"
                                        >
                                            <Col>
                                                <Skeleton.Input
                                                    style={{
                                                        width: 100,
                                                        backgroundColor:
                                                            token.colorPalette
                                                                .textColor
                                                                .tertiary,
                                                    }}
                                                    active
                                                    size="small"
                                                />
                                            </Col>
                                            <Col>
                                                <Skeleton.Button
                                                    style={{
                                                        width: 70,
                                                        backgroundColor:
                                                            token.colorPalette
                                                                .textColor
                                                                .tertiary,
                                                    }}
                                                    active
                                                    size="small"
                                                />
                                            </Col>
                                        </Row>
                                    </Card>
                                </Col>
                            ))}
                        </Row>
                    </div>
                ) : (
                    <Row
                        gutter={[16, 16]}
                        style={{
                            marginTop: '20px',
                        }}
                    >
                        {permissions?.length > 0 ? (
                            [...permissions]
                                .sort((a, b) => a.order - b.order)
                                .map((item, index) => (
                                    <Col span={24} key={item?.name}>
                                        <Card
                                            hoverable
                                            style={{
                                                borderRadius: 10,
                                                boxShadow:
                                                    '0 8px 24px rgba(0,0,0,0.05)',
                                                border: '1px solid #f0f0f0',
                                                padding: '8px 12px',
                                                transition: 'all 0.3s',
                                            }}
                                            styles={{
                                                body: {
                                                    padding: 0,
                                                },
                                            }}
                                        >
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    justifyContent:
                                                        'space-between',
                                                    alignItems: 'center',
                                                    flexWrap: 'wrap',
                                                    gap: 16,
                                                }}
                                            >
                                                <img
                                                    src={item?.imageUrl}
                                                    alt={item?.name}
                                                    style={{
                                                        maxHeight:
                                                            item?.name ===
                                                            'StoryX'
                                                                ? '15px'
                                                                : '12px',
                                                        maxWidth: '100%',
                                                        objectFit: 'contain',
                                                    }}
                                                />

                                                <Segmented
                                                    className="custom-segmented"
                                                    value={
                                                        item?.isAllowed
                                                            ? 'On'
                                                            : 'Off'
                                                    }
                                                    options={[
                                                        {
                                                            label: (
                                                                <span
                                                                    style={{
                                                                        fontWeight: 600,
                                                                    }}
                                                                >
                                                                    On
                                                                </span>
                                                            ),
                                                            value: 'On',
                                                        },
                                                        {
                                                            label: (
                                                                <span
                                                                    style={{
                                                                        fontWeight: 600,
                                                                    }}
                                                                >
                                                                    Off
                                                                </span>
                                                            ),
                                                            value: 'Off',
                                                        },
                                                    ]}
                                                    onChange={(value) =>
                                                        handlePermissionChange(
                                                            index,
                                                            value
                                                        )
                                                    }
                                                    block
                                                    style={{
                                                        backgroundColor:
                                                            '#F0F2FF',
                                                        // borderRadius: 999,
                                                        padding: 4,
                                                        minWidth: 120,
                                                    }}
                                                    size="middle"
                                                />
                                            </div>
                                        </Card>
                                    </Col>
                                ))
                        ) : (
                            <Row
                                style={{
                                    padding: '20px',
                                    width: '100%',
                                }}
                                align={'middle'}
                                justify={'center'}
                            >
                                <NoPermissionFoundIcon />
                            </Row>
                        )}
                    </Row>
                )}
            </div>
        </ConfigProvider>
    )
}

export default EditPermission
